import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { StateScope, type StateScope as StateScopeType } from 'constants/system-messages.constants';
import type {
	ISystemMessageContactIdsValidation,
	ISystemMessageDetail,
	ISystemMessageRow,
} from 'types/system-messages.types';

interface ScopedListState {
	rows: ISystemMessageRow[];
	total: number;
	isLoading: boolean;
}

interface State {
	global: ScopedListState;
	company: ScopedListState;
	currentMessage: ISystemMessageDetail | null;
	currentMessageLoading: boolean;
	currentMessageRequestId: number | null;
	isSaving: boolean;
	isDisabling: boolean;
	error: { exist: boolean; text?: string };
	contactIdsValidation: ISystemMessageContactIdsValidation;
	contactValidationRequestId: number;
}

type SystemMessagesRootState = {
	systemMessages: State;
};

const emptyScopedList = (): ScopedListState => ({
	rows: [],
	total: 0,
	isLoading: false,
});

const initialState: State = {
	global: emptyScopedList(),
	company: emptyScopedList(),
	currentMessage: null,
	currentMessageLoading: false,
	currentMessageRequestId: null,
	isSaving: false,
	isDisabling: false,
	error: { exist: false },
	contactIdsValidation: { requestedIds: [], valid: [], invalid: [] },
	contactValidationRequestId: 0,
};

const slice = createSlice({
	name: 'systemMessages',
	initialState,
	reducers: {
		setList: (
			state,
			action: PayloadAction<{
				scope: StateScopeType;
				rows: ISystemMessageRow[];
				total: number;
			}>
		) => {
			const target = action.payload.scope === StateScope.Global ? state.global : state.company;
			target.rows = action.payload.rows;
			target.total = action.payload.total;
		},
		setLoading: (state, action: PayloadAction<{ scope: StateScopeType; loading: boolean }>) => {
			const target = action.payload.scope === StateScope.Global ? state.global : state.company;
			target.isLoading = action.payload.loading;
		},
		clearList: (state, action: PayloadAction<{ scope: StateScopeType }>) => {
			if (action.payload.scope === StateScope.Global) {
				state.global = emptyScopedList();
			} else {
				state.company = emptyScopedList();
			}
		},
		setCurrentMessage: (state, action: PayloadAction<ISystemMessageDetail | null>) => {
			state.currentMessage = action.payload;
		},
		setSaving: (state, action: PayloadAction<boolean>) => {
			state.isSaving = action.payload;
		},
		setDisabling: (state, action: PayloadAction<boolean>) => {
			state.isDisabling = action.payload;
		},
		setError: (state, action: PayloadAction<{ exist: boolean; text?: string }>) => {
			state.error = action.payload;
		},
		setCurrentMessageLoading: (state, action: PayloadAction<boolean>) => {
			state.currentMessageLoading = action.payload;
		},
		setCurrentMessageRequestId: (state, action: PayloadAction<number | null>) => {
			state.currentMessageRequestId = action.payload;
		},
		beginContactIdsValidation: (state) => {
			state.contactValidationRequestId += 1;
		},
		setContactIdsValidationIfCurrent: (
			state,
			action: PayloadAction<{ requestId: number; validation: State['contactIdsValidation'] }>
		) => {
			if (action.payload.requestId !== state.contactValidationRequestId) {
				return;
			}
			state.contactIdsValidation = action.payload.validation;
		},
		clearContactIdsValidation: (state) => {
			state.contactIdsValidation = { requestedIds: [], valid: [], invalid: [] };
		},
		markMessageInactive: (state, action: PayloadAction<number>) => {
			const patchRow = (rows: ISystemMessageRow[]) => {
				const row = rows.find((item) => item.id === action.payload);
				if (row) {
					row.isActive = false;
					row.isLive = false;
				}
			};
			patchRow(state.global.rows);
			patchRow(state.company.rows);
			if (state.currentMessage?.id === action.payload) {
				state.currentMessage.isActive = false;
				state.currentMessage.isLive = false;
			}
		},
	},
});

export const {
	setList,
	setLoading,
	clearList,
	setCurrentMessage,
	setCurrentMessageLoading,
	setCurrentMessageRequestId,
	setSaving,
	setDisabling,
	setError,
	beginContactIdsValidation,
	setContactIdsValidationIfCurrent,
	clearContactIdsValidation,
	markMessageInactive,
} = slice.actions;

export const getGlobalSystemMessages = (state: SystemMessagesRootState) => state.systemMessages.global.rows;
export const getGlobalSystemMessagesTotal = (state: SystemMessagesRootState) => state.systemMessages.global.total;
export const getGlobalSystemMessagesLoading = (state: SystemMessagesRootState) => state.systemMessages.global.isLoading;
export const getCompanySystemMessages = (state: SystemMessagesRootState) => state.systemMessages.company.rows;
export const getCompanySystemMessagesTotal = (state: SystemMessagesRootState) => state.systemMessages.company.total;
export const getCompanySystemMessagesLoading = (state: SystemMessagesRootState) => state.systemMessages.company.isLoading;
export const getCurrentSystemMessage = (state: SystemMessagesRootState) => state.systemMessages.currentMessage;
export const getCurrentMessageLoading = (state: SystemMessagesRootState) =>
	state.systemMessages.currentMessageLoading;
export const getCurrentMessageRequestId = (state: SystemMessagesRootState) =>
	state.systemMessages.currentMessageRequestId;
export const getSystemMessagesSaving = (state: SystemMessagesRootState) => state.systemMessages.isSaving;
export const getSystemMessagesDisabling = (state: SystemMessagesRootState) => state.systemMessages.isDisabling;
export const getSystemMessagesError = (state: SystemMessagesRootState) => state.systemMessages.error;
export const getContactIdsValidation = (state: SystemMessagesRootState) => state.systemMessages.contactIdsValidation;

export default slice.reducer;
